import { ReleaseEntity, SystemType } from '@agilelab/plugin-wb-builder-common';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { Entity } from '@mainstage/catalog-model';
import { Content, Progress } from '@mainstage/core-components';
import { useApi, useRouteRefParams } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { Box } from '@material-ui/core';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { releaseDetailRouteRef } from '../../routes';
import { AboutCard } from './AboutCard';
import { DeployStepDetailDrawer } from './DeployStepDetailDrawer';
import { DeploymentPreviewContextProvider } from './DeploymentPreview/context/DeploymentPreviewContext';
import { DescriptorDrawer } from './DescriptorDrawer';
import { LogsDrawer } from './LogsDrawer';
import { ProvisioningCard } from './ProvisioningCard/ProvisioningCard';
import { ReleaseDetailHeader } from './ReleaseDetailHeader';
import { ReleaseDetailPageEnvironmentLoader } from './ReleaseDetailPageEnvironmentLoader';
import { ValidationTestDrawer } from './ValidationTestDrawer/ValidationTestDrawer';
import { ReleaseDetailPageContextProvider } from './context/ReleaseDetailPageContext';
import { useReleaseDetailPageContext } from './context/useReleaseDetailPageContext';
import { WbPage } from '@agilelab/plugin-wb-platform';
import { DeploymentPreviewDrawer } from './DeploymentPreview/DeploymentPreviewDrawer';

const LatestProvisioningValidationTestDrawer: FC = () => {
  const {
    tests,
    release,
    queryParamVersion,
    isValidationTestDrawerOpen,
    setIsValidationTestDrawerOpen,
  } = useReleaseDetailPageContext();
  return (
    <ValidationTestDrawer
      open={isValidationTestDrawerOpen}
      onClose={() => {
        setIsValidationTestDrawerOpen(false);
      }}
      tests={tests}
      version={queryParamVersion ?? release.metadata.version}
      projectName={
        release.metadata.projectName ?? release.metadata.dataProductName
      }
    />
  );
};

export const ReleaseDetailPage: FC = () => {
  const { kind, namespace, name } = useRouteRefParams(releaseDetailRouteRef);
  const [params] = useSearchParams();
  const env = params.get('env');
  const version = params.get('version');

  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const practiceShaperApi = useApi(practiceShaperApiRef);

  const [loading, setLoading] = useState<boolean>(false);
  const [entity, setEntity] = useState<Entity | undefined>();
  const [systemType, setSystemType] = useState<SystemType | undefined>();
  const [release, setRelease] = useState<ReleaseEntity | undefined>();

  const fetchRelease = useCallback(async () => {
    setLoading(true);
    try {
      const r = (await catalogApi.getEntityByRef({
        kind,
        namespace,
        name,
      })) as ReleaseEntity;

      const [entityRes, systemTypeRes] = await Promise.all([
        catalogApi.getEntityByRef({
          kind: r.metadata.projectKind ?? 'system',
          namespace: 'default',
          name: r.metadata.projectName ?? r.metadata.dataProductName,
        }),
        practiceShaperApi.resolveSystemTypeOf({
          systemRef: {
            name: r.metadata.projectName ?? r.metadata.dataProductName,
          },
        }),
      ]);

      setRelease(r);
      setEntity(entityRes);
      setSystemType(systemTypeRes);
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [catalogApi, kind, namespace, name, alertApi, practiceShaperApi]);

  useEffect(() => {
    fetchRelease();
  }, [fetchRelease]);

  if (loading) return <Progress />;

  return (
    <>
      {release && entity && systemType && (
        <ReleaseDetailPageContextProvider
          release={release}
          entity={entity}
          systemType={systemType}
          fetchRelease={fetchRelease}
          queryParamEnvironment={env}
          queryParamVersion={version}
        >
          <WbPage themeId="home">
            <ReleaseDetailHeader />
            <ReleaseDetailPageEnvironmentLoader>
              <Content>
                <AboutCard />
                <Box marginBottom="16px" />
                <ProvisioningCard />
              </Content>
              <DeployStepDetailDrawer />
              <LogsDrawer />
              <LatestProvisioningValidationTestDrawer />
              <DescriptorDrawer />

              <DeploymentPreviewContextProvider>
                <DeploymentPreviewDrawer />
              </DeploymentPreviewContextProvider>
            </ReleaseDetailPageEnvironmentLoader>
          </WbPage>
        </ReleaseDetailPageContextProvider>
      )}
    </>
  );
};
